import { Box, Button, Typography, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import 'react-quill/dist/quill.snow.css';

import NewFileUploader from '@components/NewFileUploader';
import { ErrorMessage } from '@components/shared';
import { Editor } from '@components/shared/HtmlEditor/HtmlEditor';
import { ApiResponse, http } from '@config/api';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { mutate } from 'swr';
import * as Yup from 'yup';
import { useGetCurrentStepData } from '../store/findSpecialistHooks';
import { submitFarmProblems, updateFarmProblems } from '../store/findSpecialistService';
import { useExpressStore } from '../store/findSpecialistStore';

const ALLOWED_FILE_FORMATS = [
	'.txt',
	'.doc',
	'.docx',
	'.pdf',
	'.xls',
	'.xlsx',
	'.ppt',
	'.kmz',
	'.kml',
	'.jpg',
	'.jpeg',
	'.png',
	'.gif',
	'.heic',
	'.webp',
	'.heif',
	'.mp4',
	'.avi',
	'.mov',
	'.wmv',
	'.flv',
	'.mpeg',
	'.mpg',
	'.webm',
	'.m4v',
	'.m4a',
	'.m4b',
	'.m4p',
	'.m4v',
	'.m4a',
	'.m4b',
];

export const DescribeFarmProblems = () => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { requestId: urlRequestId } = useParams();
	const { expressData } = useExpressStore();
	const { pathname } = useLocation();
	const isFindSpecialistForm = useMemo(() => {
		return !pathname.includes('express');
	}, [pathname]);
	const [uploadedFiles, setUploadedFiles] = useState<any>([]);
	const [isInitialized, setIsInitialized] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);

	const buildBackRoute = useMemo(() => {
		if (!isFindSpecialistForm) {
			return `/farmer/services/express/search/${urlRequestId}`;
		}

		return `/farmer/services/specialist-request/search/${urlRequestId}`;
	}, [isFindSpecialistForm, urlRequestId]);

	const buildNextRoute = useMemo(() => {
		if (!isFindSpecialistForm) {
			return `/farmer/services/express/review-and-send/${urlRequestId}`;
		}

		return `/farmer/services/specialist-request/review-and-send/${urlRequestId}`;
	}, [isFindSpecialistForm, urlRequestId]);

	const { data } = useGetCurrentStepData(urlRequestId || '');

	const formik = useFormik({
		initialValues: {
			problem_description: data?.data?.problem_description || '',
			request_files: [''],
		},
		validationSchema: Yup.object({
			problem_description: Yup.string().required('fieldNameError'),
		}),
		onSubmit: async (values) => {
			setIsSubmitting(true);
			try {
				await submitFarmProblems(
					urlRequestId || '',
					values.problem_description,
					uploadedFiles.map((file: any) => file.s3_key || file)
				);
				navigateWithLanguage(buildNextRoute);
			} catch (error) {
				console.error('Error submitting farm problems:', error);
			} finally {
				setIsSubmitting(false);
			}
		},
	});
	const { setFieldValue, isValid } = formik;

	// Initialize uploadedFiles with data from API
	useEffect(() => {
		if (data?.data?.request_files && data.data.request_files.length > 0 && !isInitialized) {
			setUploadedFiles(data.data.request_files);
			setIsInitialized(true);
		}
	}, [data, isInitialized]);

	// useEffect(() => {
	// 	if (expressData) {
	// 		if (expressData.problem_description) {
	// 			const textOnly = expressData.problem_description.replace(/<(.|\n)*?>/g, '').trim();
	// 			if (textOnly === '') {
	// 				setFieldValue('problem_description', null);
	// 			} else {
	// 				setFieldValue('request_files', expressData.request_files);
	// 				setFieldValue('problem_description', textOnly);
	// 			}
	// 		}
	// 	}
	// }, [expressData, setFieldValue]);

	const attachFileToRequest = async (fileKey: string, requestId: string) => {
		try {
			const response = await http.post<ApiResponse<any>>(
				'requests',
				`attach-request-file/${requestId}`,
				{
					userFiles: [
						{
							file: fileKey,
						},
					],
				}
			);
			return response.data;
		} catch (error) {
			console.error('Error attaching file to request:', error);
			return false;
		}
	};

	const deleteFileFromRequest = async (fileId: string) => {
		try {
			await http.delete<ApiResponse<any>>('requests', `delete-file/${fileId}`);
			return true;
		} catch (error) {
			console.error('Error deleting file:', error);
			return false;
		}
	};

	// Helper function to check if a file already exists based on s3_key
	const isFileAlreadyUploaded = (key: string) => {
		return uploadedFiles.some(
			(file: any) =>
				(file.s3_key && file.s3_key === key) || (typeof file === 'string' && file === key)
		);
	};

	const handleUploaded = async (status: any) => {
		if (status && status.key) {
			// Skip if this file is already in the uploaded files
			if (isFileAlreadyUploaded(status.key)) {
				return;
			}

			// Update local state first for instant UI feedback
			const newFile = {
				id: '',
				fileType: 'document',
				s3_key: status.key,
				filename: status.originalName || status.key,
			};

			setUploadedFiles((prev: any[]) => [...prev, newFile]);

			// Then attach the file to the request
			if (urlRequestId) {
				const result = await attachFileToRequest(status.key, urlRequestId);

				// If we received updated file data from the server, update the state
				if (result && result.data && result.data.request_files) {
					// Use the server's response to keep state in sync
					setUploadedFiles(result.data.request_files);
				}
			}
		}
	};

	const handleExistingFileRemove = async (fileId: string) => {
		if (fileId) {
			const success = await deleteFileFromRequest(fileId);

			// If deletion was successful, update the local state
			if (success) {
				// Filter out the file with the given ID
				const updatedFiles = uploadedFiles.filter((file: any) => file?.id !== fileId);
				setUploadedFiles(updatedFiles);
			}
		}
	};

	const handlePreviousClick = () => {
		// Check the API data
		const apiDescription = data?.data?.problem_description;
		const isApiEmpty = !apiDescription || apiDescription === '<p><br></p>' || apiDescription.trim() === '';
		
		// Check the form data
		const formDescription = formik.values.problem_description;
		const isFormEmpty = !formDescription || formDescription === '<p><br></p>' || formDescription.trim() === '';
		
		console.log('API description:', apiDescription);
		console.log('Is API empty:', isApiEmpty);
		console.log('Form description:', formDescription);
		console.log('Is form empty:', isFormEmpty);
		
		// Show the modal if the API data is empty
		// This means no data has been saved to the server yet
		if (isApiEmpty) {
			console.log('API data is empty, showing confirmation modal');
			setShowConfirmationModal(true);
		} else {
			// If API data is not empty, we have data on the server, so save any new changes and navigate back
			console.log('API data is not empty, saving and navigating back');
			handleSaveAndGoBack();
		}
	};

	const handleSaveAndGoBack = async () => {
		try {
			setIsSubmitting(true);
			console.log('Saving form data before navigating back...');
			
			// Use updateFarmProblems to save the current state
			const response = await updateFarmProblems(
				urlRequestId || '',
				formik.values.problem_description,
				uploadedFiles.map((file: any) => file.s3_key || file)
			);
			
			console.log('Form data saved successfully:', response);
			
			// Invalidate the SWR cache for this request to force a fresh fetch when returning to the search page
			await mutate(['getCurrentStepData', urlRequestId]);
			
			// Add a small delay to ensure the cache is invalidated before navigating
			setTimeout(() => {
				console.log('Navigating back to:', buildBackRoute);
				navigateWithLanguage(buildBackRoute);
			}, 100);
		} catch (error) {
			console.error('Error saving form data before navigating back:', error);
			// Still navigate back even if save fails
			navigateWithLanguage(buildBackRoute);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleConfirmGoBack = () => {
		setShowConfirmationModal(false);
		navigateWithLanguage(buildBackRoute);
	};

	const handleCancelGoBack = () => {
		setShowConfirmationModal(false);
	};

	return (
		<div className="sm:pb-[20px] h-auto p-[20px] sm:h-full flex flex-col gap-[20px] pb-5 justify-between mt-[20px]">
			<Box className="flex flex-col gap-[20px] pb-5 items-center justify-center">
				<Typography
					variant="h1"
					className="text-dark text-center font-roboto text-xl font-bold leading-[140%]"
					mt={4}
					mb={2}
				>
					{t('findSpecialistPage.describeFarmProblemsText')}
				</Typography>
				<Box className="max-w-[547px] gap-[20px] flex flex-col items-start justify-center">
					<Editor
						value={formik.values.problem_description}
						onBlur={() => formik.setFieldTouched('problem_description', true)}
						placeholder={`${t('describeYourNeeds')} *`}
						onChange={(value) => formik.setFieldValue('problem_description', value)}
					/>
					{formik.touched.problem_description && formik.errors.problem_description && (
						<ErrorMessage
							message={t(String(formik.errors.problem_description), {
								fieldName: t('problem_description'),
							})}
						/>
					)}
					<div className="flex flex-col gap-[10px]">
						<NewFileUploader
							title={t('myFarm.attachFile')}
							onUploaded={handleUploaded}
							description={t('findSpecialistPage.attachFileText')}
							allowedFormats={ALLOWED_FILE_FORMATS}
							showRemoveButton={true}
							multiple={true}
							showSupportedFormats={true}
							existingFiles={uploadedFiles?.map((file: any) => ({
								id: file.id || '',
								fileType: file.fileType || 'document',
								s3_key: file.s3_key || file.toString(),
								filename: file.filename || file.toString().split('/').pop() || '',
							}))}
							onExistingFileRemove={handleExistingFileRemove}
						/>
					</div>
				</Box>
			</Box>
			<Box className="flex items-center justify-between gap-[20px] mb-[20px]">
				<Button
					variant="text"
					onClick={handlePreviousClick}
					startIcon={<Icon path={mdiChevronLeft} size={1} />}
					disabled={isSubmitting}
				>
					{t('previous')}
				</Button>
				<Button
					disabled={!isValid || isSubmitting}
					onClick={(e) => formik.handleSubmit(e as any)}
					variant="contained"
					endIcon={!isSubmitting && <Icon path={mdiChevronRight} size={1} />}
				>
					{isSubmitting ? (
						<>
							<CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
							{t('processing')}
						</>
					) : (
						t('next')
					)}
				</Button>
			</Box>

			<Dialog
    open={showConfirmationModal}
    onClose={handleCancelGoBack}
    PaperProps={{
        sx: {
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            padding: '16px',
        }
    }}
>
    <DialogTitle sx={{ fontWeight: 'bold', color: '#343A40' }}>
        {t('confirmGoBackTitle')}
    </DialogTitle>
    <DialogContent>
        <DialogContentText sx={{ color: '#343A40' }}>
            {t('confirmGoBackText')}
        </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ padding: '16px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <Button 
            onClick={handleConfirmGoBack} 
            variant="outlined"
            sx={{
                borderColor: '#3DA08F',
                color: '#3DA08F',
                '&:hover': {
                    borderColor: '#2C7A6B',
                    backgroundColor: 'rgba(61, 160, 143, 0.04)',
                }
            }}
        >
            {t('confirm')}
        </Button>
        <Button 
            onClick={handleCancelGoBack} 
            variant="contained"
            sx={{
                backgroundColor: '#3DA08F',
                color: 'white',
                '&:hover': {
                    backgroundColor: '#2C7A6B',
                }
            }}
        >
            {t('cancel')}
        </Button>
    </DialogActions>
</Dialog>
		</div>
	);
};
